import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { resolveImageUrl } from "../../utils/imageUrl";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import {
  Trophy, Plus, Pencil, Trash2, ChevronDown, ChevronUp, Clock, BookOpen,
  Users, Calendar, Play, Pause, Eye, AlertCircle, Save, X, GripVertical,
  RotateCcw, History, Send, Award, Mail, Star, UserCheck
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function QuizChallengeTab({ darkMode }) {
  const { token } = useAuth();
  const dm = darkMode;
  const headers = { Authorization: `Bearer ${token}` };

  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [expandedChallenge, setExpandedChallenge] = useState(null);
  const [challengeForm, setChallengeForm] = useState({
    title: "", description: "", status: "draft", season_number: 1,
    season_duration_months: 2, start_date: "", end_date: "",
  });

  // Subject state
  const [showSubjectDialog, setShowSubjectDialog] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [subjectForm, setSubjectForm] = useState({
    subject_name: "", description: "", timer_minutes: 30, difficulty: "medium", sort_order: 0, is_enabled: true,
  });
  const [subjects, setSubjects] = useState({});

  // Questions state
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [questions, setQuestions] = useState({});
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: "", option_a: "", option_b: "", option_c: "", option_d: "", correct_option: "a", sort_order: 0,
  });
  const [activeSubjectId, setActiveSubjectId] = useState(null);

  // Leaderboard state
  const [showLeaderboard, setShowLeaderboard] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);

  const fetchChallenges = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/quiz-challenge/admin/challenges`, { headers });
      setChallenges(res.data.challenges);
    } catch (err) {
      toast.error("Failed to load challenges");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const fetchSubjects = async (challengeId) => {
    try {
      const res = await axios.get(`${API}/quiz-challenge/admin/challenges/${challengeId}/subjects`, { headers });
      setSubjects(prev => ({ ...prev, [challengeId]: res.data.subjects }));
    } catch (err) { toast.error("Failed to load subjects"); }
  };

  const fetchQuestions = async (subjectId) => {
    try {
      const res = await axios.get(`${API}/quiz-challenge/admin/subjects/${subjectId}/questions`, { headers });
      setQuestions(prev => ({ ...prev, [subjectId]: res.data.questions }));
    } catch (err) { toast.error("Failed to load questions"); }
  };

  // ---- Challenge CRUD ----
  const saveChallenge = async () => {
    if (!challengeForm.title.trim()) { toast.error("Title is required"); return; }
    try {
      if (editingChallenge) {
        await axios.put(`${API}/quiz-challenge/admin/challenges/${editingChallenge}`, challengeForm, { headers });
        toast.success("Challenge updated");
      } else {
        await axios.post(`${API}/quiz-challenge/admin/challenges`, challengeForm, { headers });
        toast.success("Challenge created");
      }
      setShowChallengeDialog(false);
      setEditingChallenge(null);
      fetchChallenges();
    } catch (err) { toast.error(err.response?.data?.detail || "Error saving"); }
  };

  const deleteChallenge = async (id) => {
    if (!window.confirm("Delete this challenge and all its data?")) return;
    try {
      await axios.delete(`${API}/quiz-challenge/admin/challenges/${id}`, { headers });
      toast.success("Challenge deleted");
      fetchChallenges();
    } catch (err) { toast.error("Delete failed"); }
  };

  // ---- Subject CRUD ----
  const saveSubject = async (challengeId) => {
    if (!subjectForm.subject_name.trim()) { toast.error("Subject name required"); return; }
    try {
      if (editingSubject) {
        await axios.put(`${API}/quiz-challenge/admin/subjects/${editingSubject}`, subjectForm, { headers });
        toast.success("Subject updated");
      } else {
        await axios.post(`${API}/quiz-challenge/admin/challenges/${challengeId}/subjects`, subjectForm, { headers });
        toast.success("Subject added");
      }
      setShowSubjectDialog(false);
      setEditingSubject(null);
      fetchSubjects(challengeId);
    } catch (err) { toast.error("Error saving subject"); }
  };

  const deleteSubject = async (subjectId, challengeId) => {
    if (!window.confirm("Delete this subject and all its questions?")) return;
    try {
      await axios.delete(`${API}/quiz-challenge/admin/subjects/${subjectId}`, { headers });
      toast.success("Subject deleted");
      fetchSubjects(challengeId);
    } catch (err) { toast.error("Delete failed"); }
  };

  // ---- Question CRUD ----
  const saveQuestion = async () => {
    const f = questionForm;
    if (!f.question_text || !f.option_a || !f.option_b || !f.option_c || !f.option_d) {
      toast.error("All fields required"); return;
    }
    try {
      if (editingQuestion) {
        await axios.put(`${API}/quiz-challenge/admin/questions/${editingQuestion}`, f, { headers });
        toast.success("Question updated");
      } else {
        await axios.post(`${API}/quiz-challenge/admin/subjects/${activeSubjectId}/questions`, f, { headers });
        toast.success("Question added");
      }
      setShowQuestionDialog(false);
      setEditingQuestion(null);
      fetchQuestions(activeSubjectId);
    } catch (err) { toast.error("Error saving question"); }
  };

  const deleteQuestion = async (questionId, subjectId) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await axios.delete(`${API}/quiz-challenge/admin/questions/${questionId}`, { headers });
      toast.success("Question deleted");
      fetchQuestions(subjectId);
    } catch (err) { toast.error("Delete failed"); }
  };

  // ---- Leaderboard ----
  const viewLeaderboard = async (challengeId) => {
    setLeaderboardData(null);
    setShowLeaderboard(challengeId);
    try {
      const res = await axios.get(`${API}/quiz-challenge/admin/challenges/${challengeId}/top-performers`, { headers });
      setLeaderboardData(res.data);
    } catch (err) { toast.error("Failed to load leaderboard"); setShowLeaderboard(null); }
  };

  // ---- Season Management ----
  const [showResetDialog, setShowResetDialog] = useState(null);
  const [resetForm, setResetForm] = useState({ status: "upcoming", season_duration_months: 2, start_date: "", end_date: "" });
  const [showSeasonHistory, setShowSeasonHistory] = useState(null);
  const [seasonHistory, setSeasonHistory] = useState([]);
  const [showStudentPerf, setShowStudentPerf] = useState(null);
  const [studentPerfData, setStudentPerfData] = useState(null);
  const [showSendMessage, setShowSendMessage] = useState(null);
  const [messageForm, setMessageForm] = useState({ subject: "", message: "" });

  const resetSeason = async (challengeId) => {
    try {
      const res = await axios.post(`${API}/quiz-challenge/admin/challenges/${challengeId}/reset-season`, resetForm, { headers });
      toast.success(`Season ${res.data.season_number} created!`);
      setShowResetDialog(null);
      fetchChallenges();
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
  };

  const viewSeasonHistory = async (challengeId) => {
    setSeasonHistory([]);
    setShowSeasonHistory(challengeId);
    try {
      const res = await axios.get(`${API}/quiz-challenge/admin/challenges/${challengeId}/season-history`, { headers });
      setSeasonHistory(res.data.seasons);
    } catch (err) { toast.error("Failed to load history"); setShowSeasonHistory(null); }
  };

  const viewStudentPerformance = async (userId) => {
    setStudentPerfData(null);
    setShowStudentPerf(userId);
    try {
      const res = await axios.get(`${API}/quiz-challenge/admin/student/${userId}/performance`, { headers });
      setStudentPerfData(res.data);
    } catch (err) { toast.error("Failed to load performance"); setShowStudentPerf(null); }
  };

  const sendMessageToStudent = async () => {
    if (!messageForm.message.trim()) { toast.error("Message is required"); return; }
    try {
      await axios.post(`${API}/quiz-challenge/admin/student/${showSendMessage}/send-message`, messageForm, { headers });
      toast.success("Message sent!");
      setShowSendMessage(null);
      setMessageForm({ subject: "", message: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
  };

  const statusColor = (s) => {
    const map = { draft: "bg-gray-500", upcoming: "bg-blue-500", active: "bg-green-500", ended: "bg-red-500" };
    return map[s] || "bg-gray-500";
  };

  const difficultyColor = (d) => {
    const map = { easy: "text-green-600", medium: "text-yellow-600", hard: "text-red-600" };
    return map[d] || "";
  };

  const cardCls = `rounded-2xl border ${dm ? "bg-gray-800/80 border-gray-700" : "bg-white border-gray-200"} shadow-sm`;
  const labelCls = `text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`;
  const inputCls = `rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`;

  return (
    <div className="space-y-6" data-testid="quiz-challenge-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${dm ? "text-white" : "text-gray-900"}`}>
            <Trophy className="inline-block w-6 h-6 mr-2 text-yellow-500" />Quiz Challenge Manager
          </h2>
          <p className={`text-sm mt-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>
            Create and manage quiz challenges, subjects, questions, and leaderboards
          </p>
        </div>
        <Button data-testid="create-challenge-btn" className="rounded-xl gap-2" onClick={() => {
          setChallengeForm({ title: "", description: "", status: "draft", season_number: 1, season_duration_months: 2, start_date: "", end_date: "" });
          setEditingChallenge(null);
          setShowChallengeDialog(true);
        }}>
          <Plus className="w-4 h-4" /> New Challenge
        </Button>
      </div>

      {/* Challenge List */}
      {loading ? (
        <div className={`text-center py-12 ${dm ? "text-gray-400" : "text-gray-500"}`}>Loading challenges...</div>
      ) : challenges.length === 0 ? (
        <Card className={cardCls}>
          <CardContent className="py-16 text-center">
            <Trophy className={`w-12 h-12 mx-auto mb-4 ${dm ? "text-gray-600" : "text-gray-300"}`} />
            <p className={`text-lg font-medium ${dm ? "text-gray-400" : "text-gray-500"}`}>No challenges yet</p>
            <p className={`text-sm ${dm ? "text-gray-500" : "text-gray-400"}`}>Create your first quiz challenge to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {challenges.map(ch => (
            <Card key={ch.id} className={cardCls}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className={`text-lg ${dm ? "text-white" : ""}`}>{ch.title}</CardTitle>
                    <span className={`text-xs px-2 py-0.5 rounded-full text-white ${statusColor(ch.status)}`}>{ch.status}</span>
                    <span className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>Season {ch.season_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className={`rounded-xl gap-1 ${dm ? "border-gray-600 text-gray-300 hover:text-white" : ""}`}
                      data-testid={`view-leaderboard-${ch.id}`} onClick={() => viewLeaderboard(ch.id)}>
                      <Eye className="w-3.5 h-3.5" /> Leaderboard
                    </Button>
                    <Button variant="outline" size="sm" className={`rounded-xl gap-1 ${dm ? "border-gray-600 text-gray-300 hover:text-white" : ""}`} onClick={() => viewSeasonHistory(ch.id)}>
                      <History className="w-3.5 h-3.5" /> History
                    </Button>
                    {(ch.status === "active" || ch.status === "ended") && (
                      <Button variant="outline" size="sm" className={`rounded-xl gap-1 text-orange-500 hover:text-orange-600 ${dm ? "border-gray-600" : ""}`} onClick={() => {
                        setResetForm({ status: "upcoming", season_duration_months: ch.season_duration_months || 2, start_date: "", end_date: "" });
                        setShowResetDialog(ch.id);
                      }}>
                        <RotateCcw className="w-3.5 h-3.5" /> New Season
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className={`rounded-xl ${dm ? "border-gray-600 text-gray-300 hover:text-white" : ""}`} onClick={() => {
                      setChallengeForm({ title: ch.title, description: ch.description || "", status: ch.status, season_number: ch.season_number, season_duration_months: ch.season_duration_months, start_date: ch.start_date || "", end_date: ch.end_date || "" });
                      setEditingChallenge(ch.id);
                      setShowChallengeDialog(true);
                    }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className={`rounded-xl text-red-500 hover:text-red-600 ${dm ? "border-gray-600" : ""}`} onClick={() => deleteChallenge(ch.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className={dm ? "text-gray-300" : ""} onClick={() => {
                      if (expandedChallenge === ch.id) { setExpandedChallenge(null); }
                      else { setExpandedChallenge(ch.id); fetchSubjects(ch.id); }
                    }}>
                      {expandedChallenge === ch.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className={`flex gap-6 text-xs mt-2 ${dm ? "text-gray-400" : "text-gray-500"}`}>
                  <span><BookOpen className="w-3.5 h-3.5 inline mr-1" />{ch.subject_count} subjects</span>
                  <span><Users className="w-3.5 h-3.5 inline mr-1" />{ch.participant_count} participants</span>
                  <span><Calendar className="w-3.5 h-3.5 inline mr-1" />{ch.season_duration_months} month season</span>
                  {ch.start_date && <span>Starts: {new Date(ch.start_date).toLocaleDateString()}</span>}
                </div>
              </CardHeader>

              {/* Expanded: Subjects */}
              {expandedChallenge === ch.id && (
                <CardContent className="pt-0">
                  <div className={`border-t ${dm ? "border-gray-700" : "border-gray-200"} pt-4`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className={`font-semibold text-sm ${dm ? "text-gray-200" : "text-gray-700"}`}>Subjects</h4>
                      <Button size="sm" variant="outline" className={`rounded-xl gap-1 ${dm ? "border-gray-600 text-gray-300 hover:text-white" : ""}`} onClick={() => {
                        setSubjectForm({ subject_name: "", description: "", timer_minutes: 30, difficulty: "medium", sort_order: (subjects[ch.id]?.length || 0), is_enabled: true });
                        setEditingSubject(null);
                        setShowSubjectDialog(ch.id);
                      }}>
                        <Plus className="w-3.5 h-3.5" /> Add Subject
                      </Button>
                    </div>

                    {(!subjects[ch.id] || subjects[ch.id].length === 0) ? (
                      <p className={`text-sm py-4 text-center ${dm ? "text-gray-500" : "text-gray-400"}`}>No subjects yet. Add subjects with questions to build this challenge.</p>
                    ) : (
                      <div className="space-y-2">
                        {subjects[ch.id].map(sub => (
                          <div key={sub.id} className={`rounded-xl border p-3 ${dm ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className={`font-medium text-sm ${dm ? "text-gray-200" : "text-gray-800"}`}>{sub.subject_name}</span>
                                <span className={`text-xs font-medium capitalize ${difficultyColor(sub.difficulty)}`}>{sub.difficulty}</span>
                                <span className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}><Clock className="w-3 h-3 inline mr-0.5" />{sub.timer_minutes} min</span>
                                <span className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>{sub.question_count || 0} questions</span>
                                {!sub.is_enabled && <Badge variant="outline" className="text-xs text-red-500">Disabled</Badge>}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" className={dm ? "text-gray-300 hover:text-white" : ""} onClick={() => {
                                  if (expandedSubject === sub.id) { setExpandedSubject(null); }
                                  else { setExpandedSubject(sub.id); fetchQuestions(sub.id); }
                                }}>
                                  {expandedSubject === sub.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                </Button>
                                <Button variant="ghost" size="sm" className={dm ? "text-gray-300 hover:text-white" : ""} onClick={() => {
                                  setSubjectForm({ subject_name: sub.subject_name, description: sub.description || "", timer_minutes: sub.timer_minutes, difficulty: sub.difficulty, sort_order: sub.sort_order, is_enabled: sub.is_enabled });
                                  setEditingSubject(sub.id);
                                  setShowSubjectDialog(ch.id);
                                }}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => deleteSubject(sub.id, ch.id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>

                            {/* Questions list */}
                            {expandedSubject === sub.id && (
                              <div className={`mt-3 pt-3 border-t ${dm ? "border-gray-600" : "border-gray-200"}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`text-xs font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Questions</span>
                                  <Button size="sm" variant="outline" className={`rounded-xl gap-1 text-xs ${dm ? "border-gray-500 text-gray-300 hover:text-white" : ""}`} onClick={() => {
                                    setQuestionForm({ question_text: "", option_a: "", option_b: "", option_c: "", option_d: "", correct_option: "a", sort_order: (questions[sub.id]?.length || 0) });
                                    setEditingQuestion(null);
                                    setActiveSubjectId(sub.id);
                                    setShowQuestionDialog(true);
                                  }}>
                                    <Plus className="w-3 h-3" /> Add Question
                                  </Button>
                                </div>
                                {(!questions[sub.id] || questions[sub.id].length === 0) ? (
                                  <p className={`text-xs py-3 text-center ${dm ? "text-gray-500" : "text-gray-400"}`}>No questions added yet</p>
                                ) : (
                                  <div className="space-y-2 max-h-80 overflow-y-auto">
                                    {questions[sub.id].map((q, qi) => (
                                      <div key={q.id} className={`rounded-lg p-2.5 text-xs ${dm ? "bg-gray-800 border border-gray-600" : "bg-white border border-gray-200"}`}>
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1">
                                            <p className={`font-medium mb-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>
                                              Q{qi + 1}: {q.question_text}
                                            </p>
                                            <div className="grid grid-cols-2 gap-1">
                                              {["a", "b", "c", "d"].map(opt => (
                                                <span key={opt} className={`${q.correct_option === opt ? "text-green-600 font-semibold" : dm ? "text-gray-400" : "text-gray-500"}`}>
                                                  {opt.toUpperCase()}) {q[`option_${opt}`]}
                                                  {q.correct_option === opt && " ✓"}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                          <div className="flex gap-1 shrink-0">
                                            <button className={`p-1 rounded ${dm ? "hover:bg-gray-700 text-gray-400 hover:text-white" : "hover:bg-gray-100"}`} onClick={() => {
                                              setQuestionForm({ question_text: q.question_text, option_a: q.option_a, option_b: q.option_b, option_c: q.option_c, option_d: q.option_d, correct_option: q.correct_option, sort_order: q.sort_order });
                                              setEditingQuestion(q.id);
                                              setActiveSubjectId(sub.id);
                                              setShowQuestionDialog(true);
                                            }}>
                                              <Pencil className="w-3 h-3" />
                                            </button>
                                            <button className="p-1 rounded text-red-500 hover:bg-red-50" onClick={() => deleteQuestion(q.id, sub.id)}>
                                              <Trash2 className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Challenge Dialog */}
      <Dialog open={showChallengeDialog} onOpenChange={setShowChallengeDialog}>
        <DialogContent className={`max-w-lg ${dm ? "bg-gray-800 border-gray-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className={dm ? "text-white" : ""}>{editingChallenge ? "Edit Challenge" : "New Quiz Challenge"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Title *</label>
              <Input className={inputCls} placeholder="e.g. JHS Quiz Challenge" value={challengeForm.title} onChange={e => setChallengeForm(f => ({ ...f, title: e.target.value }))} data-testid="challenge-title-input" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea className={`w-full rounded-xl border p-3 text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : "border-gray-200"}`} rows={3} placeholder="Brief description of this challenge..." value={challengeForm.description} onChange={e => setChallengeForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Status</label>
                <Select value={challengeForm.status} onValueChange={v => setChallengeForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="ended">Ended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Season Number</label>
                <Input className={inputCls} type="number" min={1} value={challengeForm.season_number} onChange={e => setChallengeForm(f => ({ ...f, season_number: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Season Duration</label>
                <Select value={String(challengeForm.season_duration_months)} onValueChange={v => setChallengeForm(f => ({ ...f, season_duration_months: parseInt(v) }))}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Month</SelectItem>
                    <SelectItem value="2">2 Months</SelectItem>
                    <SelectItem value="3">3 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Start Date</label>
                <Input className={inputCls} type="date" value={challengeForm.start_date?.slice(0, 10) || ""} onChange={e => setChallengeForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>End Date</label>
                <Input className={inputCls} type="date" value={challengeForm.end_date?.slice(0, 10) || ""} onChange={e => setChallengeForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChallengeDialog(false)} className={dm ? "border-gray-600 text-gray-300" : ""}>Cancel</Button>
            <Button onClick={saveChallenge} className="gap-2" data-testid="save-challenge-btn"><Save className="w-4 h-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subject Dialog */}
      <Dialog open={!!showSubjectDialog} onOpenChange={() => setShowSubjectDialog(false)}>
        <DialogContent className={`max-w-md ${dm ? "bg-gray-800 border-gray-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className={dm ? "text-white" : ""}>{editingSubject ? "Edit Subject" : "Add Subject"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Subject Name *</label>
              <Input className={inputCls} placeholder="e.g. Mathematics" value={subjectForm.subject_name} onChange={e => setSubjectForm(f => ({ ...f, subject_name: e.target.value }))} data-testid="subject-name-input" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <Input className={inputCls} placeholder="Brief description" value={subjectForm.description} onChange={e => setSubjectForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Timer (minutes)</label>
                <Input className={inputCls} type="number" min={1} value={subjectForm.timer_minutes} onChange={e => setSubjectForm(f => ({ ...f, timer_minutes: parseInt(e.target.value) || 30 }))} />
              </div>
              <div>
                <label className={labelCls}>Difficulty</label>
                <Select value={subjectForm.difficulty} onValueChange={v => setSubjectForm(f => ({ ...f, difficulty: v }))}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubjectDialog(false)} className={dm ? "border-gray-600 text-gray-300" : ""}>Cancel</Button>
            <Button onClick={() => saveSubject(showSubjectDialog)} className="gap-2" data-testid="save-subject-btn"><Save className="w-4 h-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className={`max-w-lg ${dm ? "bg-gray-800 border-gray-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className={dm ? "text-white" : ""}>{editingQuestion ? "Edit Question" : "Add Question"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Question *</label>
              <textarea className={`w-full rounded-xl border p-3 text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : "border-gray-200"}`} rows={2} placeholder="Enter the question..." value={questionForm.question_text} onChange={e => setQuestionForm(f => ({ ...f, question_text: e.target.value }))} data-testid="question-text-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {["a", "b", "c", "d"].map(opt => (
                <div key={opt}>
                  <label className={labelCls}>Option {opt.toUpperCase()}</label>
                  <Input className={inputCls} placeholder={`Option ${opt.toUpperCase()}`} value={questionForm[`option_${opt}`]} onChange={e => setQuestionForm(f => ({ ...f, [`option_${opt}`]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div>
              <label className={labelCls}>Correct Answer</label>
              <Select value={questionForm.correct_option} onValueChange={v => setQuestionForm(f => ({ ...f, correct_option: v }))}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="a">A</SelectItem>
                  <SelectItem value="b">B</SelectItem>
                  <SelectItem value="c">C</SelectItem>
                  <SelectItem value="d">D</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuestionDialog(false)} className={dm ? "border-gray-600 text-gray-300" : ""}>Cancel</Button>
            <Button onClick={saveQuestion} className="gap-2" data-testid="save-question-btn"><Save className="w-4 h-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leaderboard Dialog */}
      <Dialog open={!!showLeaderboard} onOpenChange={() => setShowLeaderboard(null)}>
        <DialogContent className={`max-w-3xl max-h-[80vh] overflow-y-auto ${dm ? "bg-gray-800 border-gray-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className={dm ? "text-white" : ""}>
              <Trophy className="w-5 h-5 inline mr-2 text-yellow-500" />Leaderboard
            </DialogTitle>
          </DialogHeader>
          {!leaderboardData ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : leaderboardData.leaderboard.length === 0 ? (
                <p className={`text-center py-8 ${dm ? "text-gray-400" : "text-gray-500"}`} style={{ animation: "fadeSlideIn 0.3s ease-out" }}>No participants yet</p>
              ) : (
                <div className="space-y-4" style={{ animation: "fadeSlideIn 0.3s ease-out" }}>
                  {leaderboardData.leaderboard.map((entry, i) => (
                    <div key={entry.user_id}
                      style={{ animation: `fadeSlideIn 0.3s ease-out ${i * 0.05}s both` }}
                      className={`rounded-xl border overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${i === 0 ? "border-yellow-500 bg-yellow-50/10" : i === 1 ? "border-gray-400 bg-gray-50/10" : i === 2 ? "border-orange-600 bg-orange-50/10" : dm ? "border-gray-600 bg-gray-700/30" : "border-gray-200 bg-white"}`}
                      onClick={() => viewStudentPerformance(entry.user_id)}>
                      {/* Header row */}
                      <div className="flex items-center gap-3 p-3 pb-1.5">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${i === 0 ? "bg-yellow-500 text-white" : i === 1 ? "bg-gray-400 text-white" : i === 2 ? "bg-orange-600 text-white" : dm ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}>
                          {entry.rank}
                        </div>
                        {entry.profile_picture ? (
                          <img src={resolveImageUrl(entry.profile_picture)} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm shrink-0" />
                        ) : (
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${dm ? "bg-gray-600 text-gray-300" : "bg-gradient-to-br from-gray-200 to-gray-300 text-gray-500"}`}>
                            {entry.first_name?.[0]}{entry.last_name?.[0]}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold text-sm ${dm ? "text-gray-200" : "text-gray-800"}`}>
                            {entry.first_name} {entry.middle_name || ""} {entry.last_name}
                            {leaderboardData.four_time_winners?.includes(entry.user_id) && (
                              <span className="ml-2 text-xs bg-yellow-500 text-white px-1.5 py-0.5 rounded-full">4x Champion</span>
                            )}
                          </p>
                          <p className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>
                            {entry.school_name}{entry.school_location ? ` — ${entry.school_location}` : ""}
                            {entry.class_level ? ` | ${entry.class_level}` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className={`font-bold text-xl ${dm ? "text-white" : "text-gray-900"}`}>{entry.total_percentage}<span className="text-sm text-gray-400">%</span></p>
                            <p className={`text-[10px] uppercase tracking-wide ${dm ? "text-gray-500" : "text-gray-400"}`}>Total Score</p>
                          </div>
                          <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-600" onClick={(e) => { e.stopPropagation(); setShowSendMessage(entry.user_id); setMessageForm({ subject: "Congratulations on Your Performance!", message: "" }); }}>
                            <Mail className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Subject breakdown */}
                      <div className="px-3 pb-3 pt-1">
                        <div className="grid gap-1">
                          {entry.subject_scores?.map(ss => (
                            <div key={ss.subject_id} className={`flex items-center justify-between text-xs rounded-lg px-2.5 py-1.5 ${dm ? "bg-gray-700/50 border border-gray-600" : "bg-white/60 border border-gray-100"}`}>
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${dm ? "text-gray-300" : "text-gray-700"}`}>{ss.subject_name}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize font-medium ${ss.difficulty === "easy" ? "bg-green-100 text-green-700" : ss.difficulty === "hard" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{ss.difficulty}</span>
                              </div>
                              <div className={`flex items-center gap-3 ${dm ? "text-gray-400" : "text-gray-600"}`}>
                                <span>{ss.percentage}%</span>
                                <span className={dm ? "text-gray-500" : "text-gray-400"}>{ss.score}/{ss.total_questions}</span>
                                <span className={dm ? "text-gray-500" : "text-gray-400"}>{Math.floor(ss.time_used_seconds / 60)}m {ss.time_used_seconds % 60}s</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </DialogContent>
      </Dialog>

      {/* Reset Season Dialog */}
      <Dialog open={!!showResetDialog} onOpenChange={() => setShowResetDialog(null)}>
        <DialogContent className={`max-w-md ${dm ? "bg-gray-800 border-gray-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className={dm ? "text-white" : ""}><RotateCcw className="w-5 h-5 inline mr-2 text-orange-500" />Start New Season</DialogTitle>
          </DialogHeader>
          <p className={`text-sm mb-4 ${dm ? "text-gray-400" : "text-gray-500"}`}>
            This will end the current season, archive its leaderboard, and create a new season with the same subjects (without questions). You'll need to upload new questions.
          </p>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>New Season Status</label>
              <Select value={resetForm.status} onValueChange={v => setResetForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming (set start date)</SelectItem>
                  <SelectItem value="draft">Draft (configure later)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Season Duration</label>
              <Select value={String(resetForm.season_duration_months)} onValueChange={v => setResetForm(f => ({ ...f, season_duration_months: parseInt(v) }))}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="2">2 Months</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Start Date</label>
              <Input className={inputCls} type="date" value={resetForm.start_date} onChange={e => setResetForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(null)} className={dm ? "border-gray-600 text-gray-300" : ""}>Cancel</Button>
            <Button className="gap-2 bg-orange-500 hover:bg-orange-600" onClick={() => resetSeason(showResetDialog)} data-testid="confirm-reset-season">
              <RotateCcw className="w-4 h-4" /> Create New Season
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Season History Dialog */}
      <Dialog open={!!showSeasonHistory} onOpenChange={() => setShowSeasonHistory(null)}>
        <DialogContent className={`max-w-2xl max-h-[80vh] overflow-y-auto ${dm ? "bg-gray-800 border-gray-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className={dm ? "text-white" : ""}><History className="w-5 h-5 inline mr-2 text-blue-500" />Season History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {seasonHistory.map(s => (
              <div key={s.id} className={`rounded-xl p-4 border ${s.status === "active" ? "border-green-500" : dm ? "border-gray-600" : "border-gray-200"}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${dm ? "text-gray-200" : "text-gray-800"}`}>Season {s.season_number}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full text-white ${statusColor(s.status)}`}>{s.status}</span>
                  </div>
                  <span className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>{s.participant_count} participants</span>
                </div>
                {s.start_date && <p className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>{new Date(s.start_date).toLocaleDateString()} — {s.end_date ? new Date(s.end_date).toLocaleDateString() : "ongoing"}</p>}
                {s.winner && (
                  <div className="mt-2 flex items-center gap-2">
                    <Award className="w-4 h-4 text-yellow-500" />
                    <span className={`text-sm font-medium ${dm ? "text-yellow-400" : "text-yellow-700"}`}>Winner: {s.winner.name} (Total Score: {s.winner.total_percentage}%)</span>
                    <Button variant="ghost" size="sm" className="text-blue-500 ml-auto" onClick={() => viewStudentPerformance(s.winner.user_id)}>
                      <UserCheck className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {seasonHistory.length === 0 && <p className={`text-center py-6 ${dm ? "text-gray-500" : "text-gray-400"}`}>No season history yet</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Student Performance Dialog */}
      <Dialog open={!!showStudentPerf} onOpenChange={() => setShowStudentPerf(null)}>
        <DialogContent className={`max-w-2xl max-h-[80vh] overflow-y-auto ${dm ? "bg-gray-800 border-gray-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className={dm ? "text-white" : ""}><Star className="w-5 h-5 inline mr-2 text-yellow-500" />Student Performance</DialogTitle>
          </DialogHeader>
          {!studentPerfData ? (
            <div className="flex items-center justify-center py-12">
              <div className={`w-8 h-8 border-4 border-t-transparent rounded-full animate-spin ${dm ? "border-yellow-500" : "border-yellow-500"}`} />
            </div>
          ) : (
            <div style={{ animation: "fadeSlideIn 0.3s ease-out" }}>
              <div className={`rounded-xl p-4 mb-4 ${dm ? "bg-gray-700" : "bg-gray-50"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-semibold text-lg ${dm ? "text-white" : "text-gray-900"}`}>
                      {studentPerfData.profile?.first_name} {studentPerfData.profile?.middle_name || ""} {studentPerfData.profile?.last_name}
                    </p>
                    <p className={`text-sm ${dm ? "text-gray-400" : "text-gray-500"}`}>{studentPerfData.profile?.school_name}{studentPerfData.profile?.school_location ? ` — ${studentPerfData.profile.school_location}` : ""}</p>
                    {studentPerfData.user?.email && <p className={`text-xs ${dm ? "text-gray-500" : "text-gray-400"}`}>{studentPerfData.user.email}{studentPerfData.user.phone ? ` | ${studentPerfData.user.phone}` : ""}</p>}
                  </div>
                  <Button size="sm" className="rounded-xl gap-1" onClick={() => {
                    setShowSendMessage(showStudentPerf);
                    setMessageForm({ subject: "Congratulations on Your Performance!", message: "" });
                  }}>
                    <Mail className="w-3.5 h-3.5" /> Send Message
                  </Button>
                </div>
              </div>
              <h4 className={`font-semibold text-sm mb-3 ${dm ? "text-gray-300" : "text-gray-700"}`}>Quiz History</h4>
              {studentPerfData.attempts?.length === 0 ? (
                <p className={`text-center py-6 ${dm ? "text-gray-500" : "text-gray-400"}`}>No quiz attempts yet</p>
              ) : (
                <div className="space-y-2">
                  {studentPerfData.attempts.map((a, i) => (
                    <div key={i} className={`rounded-lg p-3 border text-sm ${dm ? "border-gray-600" : "border-gray-200"}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`font-medium ${dm ? "text-gray-200" : "text-gray-800"}`}>{a.subject_name}</span>
                          <span className={`text-xs ml-2 capitalize ${a.difficulty === "easy" ? "text-green-500" : a.difficulty === "hard" ? "text-red-500" : "text-yellow-500"}`}>{a.difficulty}</span>
                        </div>
                        <span className={`font-bold ${dm ? "text-white" : ""}`}>{a.score}/{a.total_questions}</span>
                      </div>
                      <div className={`flex gap-4 mt-1 text-xs ${dm ? "text-gray-500" : "text-gray-400"}`}>
                        <span>{a.challenge_title} — Season {a.season_number}</span>
                        <span>{Math.floor(a.time_used_seconds / 60)}m {a.time_used_seconds % 60}s</span>
                        {a.completed_at && <span>{new Date(a.completed_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={!!showSendMessage} onOpenChange={() => setShowSendMessage(null)}>
        <DialogContent className={`max-w-md ${dm ? "bg-gray-800 border-gray-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className={dm ? "text-white" : ""}><Send className="w-5 h-5 inline mr-2 text-green-500" />Send Message to Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Email Subject</label>
              <Input className={inputCls} placeholder="Subject" value={messageForm.subject} onChange={e => setMessageForm(f => ({ ...f, subject: e.target.value }))} data-testid="message-subject-input" />
            </div>
            <div>
              <label className={labelCls}>Message *</label>
              <textarea className={`w-full rounded-xl border p-3 text-sm min-h-[120px] ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : "border-gray-200"}`}
                placeholder="Write your message to the student..." value={messageForm.message}
                onChange={e => setMessageForm(f => ({ ...f, message: e.target.value }))} data-testid="message-body-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendMessage(null)} className={dm ? "border-gray-600 text-gray-300" : ""}>Cancel</Button>
            <Button className="gap-2" onClick={sendMessageToStudent} data-testid="send-message-btn">
              <Send className="w-4 h-4" /> Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
